// src/services/openaiService.ts
import { Staff } from '../contexts/DataContext';
import { StaffAllocationGrid } from '../types/grid';
import { TeachingIntention } from '../types/intentions';
import { CurriculumSubject } from '../utils/preferenceMatching';
import { aiProgressService } from './aiProgressService';
import { supabase } from '../supabase/config';

// NOTE (issue #27, H4): The OpenAI API key is an org-level credential and
// is NEVER stored in the browser. It lives as an Edge Function secret
// (OPENAI_API_KEY) and all OpenAI calls are proxied through the
// `openai-suggest` Supabase Edge Function. AISettings therefore holds only
// non-secret model/prompt configuration.
export interface AISettings {
  model: string;
  temperature: number;
  maxTokens: number;
  systemPrompt: string;
  analysisPrompt: string;
  assignmentPrompt: string;
}

export interface AIAssignmentSuggestion {
  classId: string;
  className: string;
  lineId: string;
  teacherCode: string;
  teacherName: string;
  confidence: number;
  reasoning: string[];
  conflictWarnings: string[];
}

export interface AIObservation {
  type: 'insight' | 'warning' | 'recommendation';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  affectedClasses?: string[];
  suggestedActions?: string[];
}

export interface AIGridSuggestion {
  suggestions: AIAssignmentSuggestion[];
  observations: AIObservation[];
  overallConfidence: number;
  processingTime: number;
  stats: {
    totalAssignments: number;
    highConfidenceAssignments: number;
    conflictsDetected: number;
    unassignableClasses: number;
  };
}

class OpenAIService {
  private settings: AISettings;
  private isPaused: boolean = false;
  
  constructor() {
    this.settings = this.getDefaultSettings();
  }

  private getDefaultSettings(): AISettings {
    return {
      model: 'gpt-4o-mini',
      temperature: 0.3,
      maxTokens: 4000,
      systemPrompt: `You are an expert timetabling assistant for a secondary school. Your role is to analyze teacher qualifications, preferences, workloads, and constraints to suggest optimal class assignments.

Key considerations:
1. Teacher qualifications and subject expertise
2. Teaching preferences and intentions 
3. Workload distribution and FTE considerations
4. Avoiding conflicts and overloading
5. Curriculum area expertise
6. Year level preferences
7. Professional development goals

Always provide specific reasoning for your suggestions and flag any potential conflicts.`,
      
      analysisPrompt: `Analyze the current timetabling situation and provide insights:

1. Workload Distribution Analysis
2. Preference Satisfaction Assessment  
3. Qualification Matching Review
4. Potential Conflicts and Issues
5. Recommendations for Improvement

Focus on actionable insights that help administrators make better decisions.`,

      assignmentPrompt: `Generate teacher assignments for the unassigned classes, considering:

1. Match teachers to their qualified subjects
2. Respect teaching preferences where possible
3. Balance workloads according to FTE
4. Avoid scheduling conflicts
5. Consider professional development opportunities
6. Maintain curriculum area expertise

Provide confidence scores (0-100) and detailed reasoning for each assignment.`
    };
  }

  updateSettings(newSettings: Partial<AISettings>) {
    this.settings = { ...this.settings, ...newSettings };
    // The OpenAI key is intentionally NOT handled here — it never touches
    // the browser. Model/prompt config is in-memory only for the session.
  }

  getSettings(): AISettings {
    return { ...this.settings };
  }

  // Check if there's resumable progress for a grid
  hasResumableProgress(gridId: string): boolean {
    return aiProgressService.hasResumableProgress(gridId);
  }

  // Get current progress details
  getProgressDetails(gridId: string) {
    const progress = aiProgressService.getProgress(gridId);
    if (!progress) return null;

    return {
      completedChunks: progress.completedChunks,
      totalChunks: progress.totalChunks,
      percentage: aiProgressService.getProgressPercentage(),
      estimatedTimeRemaining: aiProgressService.getEstimatedTimeRemaining(),
      status: progress.status,
      suggestionsCount: progress.suggestions.length,
      lastUpdateTime: progress.lastUpdateTime
    };
  }

  // Pause processing
  pauseProcessing(): void {
    this.isPaused = true;
    aiProgressService.pauseProcessing();
  }

  // Resume processing
  resumeProcessing(): void {
    this.isPaused = false;
    aiProgressService.resumeProcessing();
  }

  // Clear saved progress
  clearProgress(gridId: string): void {
    aiProgressService.clearProgress(gridId);
  }

  async generateGridSuggestions(
    grid: StaffAllocationGrid,
    staff: Staff[],
    teachingIntentions: TeachingIntention[],
    curriculumSubjects: CurriculumSubject[],
    progressCallback?: (current: number, total: number) => void
  ): Promise<AIGridSuggestion> {
    const startTime = Date.now();

    try {
      // Prepare data for AI analysis
      const unassignedClasses = this.getUnassignedClasses(grid);
      const teacherData = this.prepareTeacherData(staff, teachingIntentions, curriculumSubjects);
      const constraintsData = this.analyzeConstraints(grid, staff);

      // Process in chunks to avoid context limits
      const allSuggestions = await this.processInChunks(
        unassignedClasses,
        teacherData,
        constraintsData,
        grid,
        progressCallback
      );

      // Generate observations separately with smaller data set
      const observations = await this.generateObservations(grid, staff, teachingIntentions);

      const processingTime = Date.now() - startTime;

      return {
        suggestions: allSuggestions,
        observations,
        overallConfidence: this.calculateOverallConfidence(allSuggestions),
        processingTime,
        stats: {
          totalAssignments: allSuggestions.length,
          highConfidenceAssignments: allSuggestions.filter(s => s.confidence >= 80).length,
          conflictsDetected: allSuggestions.reduce((acc, s) => acc + s.conflictWarnings.length, 0),
          unassignableClasses: unassignedClasses.length - allSuggestions.length
        }
      };

    } catch (error) {
      console.error('OpenAI service error:', error);
      throw new Error(`AI suggestion generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async processInChunks(
    unassignedClasses: any[],
    teacherData: any[],
    constraintsData: any,
    grid: StaffAllocationGrid,
    progressCallback?: (current: number, total: number) => void
  ): Promise<AIAssignmentSuggestion[]> {
    // Chunk size - process 5 classes at a time to stay well within context limits
    const chunkSize = 5;
    const totalChunks = Math.ceil(unassignedClasses.length / chunkSize);
    
    // Initialize or resume progress
    const progress = aiProgressService.initializeProgress(grid.id, totalChunks);
    
    // If resuming, use existing suggestions
    let allSuggestions: AIAssignmentSuggestion[] = progress.suggestions || [];
    
    // Clean up expired progress before starting
    aiProgressService.cleanupExpiredProgress();

    // Create chunks with their numbers
    const chunks: Array<{ number: number; classes: any[] }> = [];
    for (let i = 0; i < unassignedClasses.length; i += chunkSize) {
      const chunkNumber = Math.floor(i / chunkSize) + 1;
      chunks.push({
        number: chunkNumber,
        classes: unassignedClasses.slice(i, i + chunkSize)
      });
    }

    // Get chunks that need processing (skips already completed ones)
    const chunksToProcess = aiProgressService.getChunksToProcess(
      chunks.map(c => c.number)
    );

    // Process remaining chunks
    for (const chunk of chunks) {
      // Check if processing was paused
      if (this.isPaused) {
        aiProgressService.pauseProcessing();
        throw new Error('Processing was paused by user');
      }

      // Skip if already processed
      if (!chunksToProcess.includes(chunk.number)) {
        if (progressCallback) {
          progressCallback(chunk.number, totalChunks);
        }
        continue;
      }

      // Filter out already processed classes
      const classesToProcess = chunk.classes.filter(cls => 
        !progress.processedClasses.has(cls.id)
      );

      if (classesToProcess.length === 0) {
        // Mark chunk as complete if all classes already processed
        aiProgressService.updateChunkProgress(
          chunk.number,
          chunk.classes.map(c => c.id),
          [],
          true
        );
        continue;
      }

      // Update progress callback
      if (progressCallback) {
        const currentProgress = aiProgressService.getCurrentProgress();
        progressCallback(
          currentProgress?.completedChunks || chunk.number,
          totalChunks
        );
      }
      
      try {
        const prompt = this.buildChunkedAssignmentPrompt(
          classesToProcess,
          teacherData,
          constraintsData,
          grid,
          chunk.number,
          totalChunks
        );

        const response = await this.callOpenAI(prompt);
        const chunkSuggestions = this.parseAISuggestions(response, classesToProcess);
        
        // Update progress with successful chunk
        aiProgressService.updateChunkProgress(
          chunk.number,
          classesToProcess.map(c => c.id),
          chunkSuggestions,
          true
        );
        
        allSuggestions.push(...chunkSuggestions);

        // Add small delay between chunks to avoid rate limiting
        if (chunk.number < totalChunks) {
          await new Promise(resolve => setTimeout(resolve, 750));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.warn(`Failed to process chunk ${chunk.number}:`, error);
        
        // Mark chunk for retry
        aiProgressService.markChunkForRetry(chunk.number, errorMessage);
        
        // Update progress with failure
        aiProgressService.updateChunkProgress(
          chunk.number,
          classesToProcess.map(c => c.id),
          [],
          false,
          errorMessage
        );
        
        // Continue with other chunks even if one fails
      }
    }

    // Clear progress on successful completion
    const finalProgress = aiProgressService.getCurrentProgress();
    if (finalProgress && finalProgress.status === 'completed') {
      aiProgressService.clearProgress(grid.id);
    }

    return allSuggestions;
  }

  async generateObservations(
    grid: StaffAllocationGrid,
    staff: Staff[],
    teachingIntentions: TeachingIntention[]
  ): Promise<AIObservation[]> {
    try {
      const analysisData = this.prepareAnalysisData(grid, staff, teachingIntentions);
      const prompt = this.buildAnalysisPrompt(analysisData);
      
      const response = await this.callOpenAI(prompt);
      return this.parseObservations(response);
    } catch (error) {
      console.error('Observation generation failed:', error);
      return this.generateFallbackObservations(grid, staff);
    }
  }

  private async callOpenAI(prompt: string): Promise<string> {
    // Proxy through the `openai-suggest` Edge Function (issue #27). The
    // org OpenAI key lives server-side as a Function secret; the browser
    // never sees it. supabase.functions.invoke attaches the signed-in
    // user's bearer token so the function can authorise the caller.
    const { data, error } = await supabase.functions.invoke('openai-suggest', {
      body: {
        model: this.settings.model,
        temperature: this.settings.temperature,
        maxTokens: this.settings.maxTokens,
        jsonMode: true,
        messages: [
          { role: 'system', content: this.settings.systemPrompt },
          { role: 'user', content: prompt },
        ],
      },
    });

    if (error) {
      throw new Error(`AI service error: ${error.message}`);
    }

    return (data as { content?: string })?.content || '';
  }

  private getUnassignedClasses(grid: StaffAllocationGrid) {
    return grid.classes.filter(cls => {
      return !Object.values(cls.lineAssignments).some(assignment => assignment);
    });
  }

  private prepareTeacherData(
    staff: Staff[],
    teachingIntentions: TeachingIntention[],
    curriculumSubjects: CurriculumSubject[]
  ) {
    return staff.map(teacher => {
      const intention = teachingIntentions.find(i => i.staffId === teacher.initial);
      const curriculumPrefs = curriculumSubjects.map(subject => 
        subject.teacherPreferences?.find(pref => pref.teacherCode === teacher.initial)
      ).filter(Boolean);

      return {
        code: teacher.initial,
        name: teacher.fullName,
        department: teacher.department,
        fte: teacher.fte,
        qualifications: teacher.qualifications || [],
        intentions: intention ? {
          preferences: intention.teachingPreferences || [],
          comfortableAreas: intention.comfortableTeachingAreas || [],
          developmentGoals: intention.professionalDevelopmentGoals || []
        } : null,
        curriculumPreferences: curriculumPrefs
      };
    });
  }

  private analyzeConstraints(grid: StaffAllocationGrid, staff: Staff[]) {
    // Analyze current assignments for constraints
    const assignments = new Map();
    
    grid.classes.forEach(cls => {
      Object.entries(cls.lineAssignments).forEach(([lineId, teacherCode]) => {
        if (teacherCode) {
          if (!assignments.has(teacherCode)) {
            assignments.set(teacherCode, []);
          }
          assignments.get(teacherCode).push({ classId: cls.id, lineId });
        }
      });
    });

    return {
      currentAssignments: Object.fromEntries(assignments),
      teacherLoads: staff.map(teacher => ({
        code: teacher.initial,
        currentLoad: assignments.get(teacher.initial)?.length || 0,
        maxLoad: Math.floor(teacher.fte * 20), // Approximate max periods
        availableLines: grid.lines.map(l => l.id)
      }))
    };
  }

  private buildChunkedAssignmentPrompt(
    classChunk: any[],
    teacherData: any[],
    constraintsData: any,
    grid: StaffAllocationGrid,
    chunkNumber: number,
    totalChunks: number
  ): string {
    // Optimize data for context limits
    const optimizedTeacherData = teacherData.map(teacher => ({
      code: teacher.code,
      name: teacher.name,
      department: teacher.department,
      fte: teacher.fte,
      // Limit qualifications and intentions to most relevant
      qualifications: (teacher.qualifications || []).slice(0, 5),
      intentions: teacher.intentions ? {
        preferences: (teacher.intentions.preferences || []).slice(0, 8),
        comfortableAreas: (teacher.intentions.comfortableAreas || []).slice(0, 5)
      } : null,
      curriculumPreferences: (teacher.curriculumPreferences || []).slice(0, 3)
    }));

    const optimizedConstraints = {
      teacherLoads: constraintsData.teacherLoads.map((load: any) => ({
        code: load.code,
        currentLoad: load.currentLoad,
        maxLoad: load.maxLoad
      }))
    };

    return `${this.settings.assignmentPrompt}

PROCESSING CHUNK ${chunkNumber} of ${totalChunks}

CLASSES TO ASSIGN (this chunk only):
${JSON.stringify(classChunk.map(cls => ({
  id: cls.id,
  className: cls.className,
  curriculumArea: cls.curriculumArea,
  yearLevel: cls.yearLevel,
  lineAssignments: cls.lineAssignments
})), null, 2)}

AVAILABLE TEACHERS (optimized):
${JSON.stringify(optimizedTeacherData.slice(0, 20), null, 2)}

CURRENT CONSTRAINTS:
${JSON.stringify(optimizedConstraints, null, 2)}

AVAILABLE LINES:
${JSON.stringify(grid.lines.map(l => ({ id: l.id, name: l.name, semester: l.semester })), null, 2)}

IMPORTANT: Only suggest assignments for the classes in this chunk. Focus on the most suitable matches.

Please respond with a JSON object containing:
{
  "suggestions": [
    {
      "classId": "class_id",
      "className": "Class Name",
      "lineId": "line_id", 
      "teacherCode": "teacher_code",
      "teacherName": "Teacher Name",
      "confidence": 85,
      "reasoning": ["reason 1", "reason 2"],
      "conflictWarnings": ["warning 1"]
    }
  ]
}`;
  }

  // Keep the original method for backward compatibility
  private buildAssignmentPrompt(
    unassignedClasses: any[],
    teacherData: any[],
    constraintsData: any,
    grid: StaffAllocationGrid
  ): string {
    return this.buildChunkedAssignmentPrompt(
      unassignedClasses,
      teacherData,
      constraintsData,
      grid,
      1,
      1
    );
  }

  private buildAnalysisPrompt(analysisData: any): string {
    return `${this.settings.analysisPrompt}

CURRENT SITUATION:
${JSON.stringify(analysisData, null, 2)}

Please respond with a JSON object containing:
{
  "observations": [
    {
      "type": "insight|warning|recommendation",
      "title": "Observation Title",
      "description": "Detailed description",
      "priority": "high|medium|low",
      "affectedClasses": ["class1", "class2"],
      "suggestedActions": ["action 1", "action 2"]
    }
  ]
}`;
  }

  private prepareAnalysisData(
    grid: StaffAllocationGrid,
    staff: Staff[],
    teachingIntentions: TeachingIntention[]
  ) {
    // Keep analysis data minimal to avoid context limits
    const unassignedCount = grid.classes.filter(cls => 
      !Object.values(cls.lineAssignments).some(assignment => assignment)
    ).length;

    const assignedClasses = grid.classes.filter(cls => 
      Object.values(cls.lineAssignments).some(assignment => assignment)
    ).length;

    // Sample some classes for analysis instead of all
    const sampleClasses = grid.classes.slice(0, 10).map(cls => ({
      className: cls.className,
      curriculumArea: cls.curriculumArea,
      yearLevel: cls.yearLevel,
      isAssigned: Object.values(cls.lineAssignments).some(assignment => assignment)
    }));

    return {
      gridStats: {
        totalClasses: grid.classes.length,
        assignedClasses,
        unassignedClasses: unassignedCount,
        totalLines: grid.lines.length,
        completionPercentage: grid.classes.length > 0 ? Math.round((assignedClasses / grid.classes.length) * 100) : 0
      },
      staffStats: {
        totalStaff: staff.length,
        departmentDistribution: this.getDepartmentDistribution(staff),
        fteDistribution: this.getFTEDistribution(staff)
      },
      intentionStats: {
        totalIntentions: teachingIntentions.length,
        preferencesCovered: this.calculatePreferencesCovered(teachingIntentions, grid)
      },
      sampleClasses // Include sample for context
    };
  }

  private parseAISuggestions(response: string, unassignedClasses: any[]): AIAssignmentSuggestion[] {
    try {
      const parsed = JSON.parse(response);
      return (parsed.suggestions || []).map((suggestion: any) => ({
        classId: suggestion.classId || '',
        className: suggestion.className || '',
        lineId: suggestion.lineId || '',
        teacherCode: suggestion.teacherCode || '',
        teacherName: suggestion.teacherName || '',
        confidence: Math.max(0, Math.min(100, suggestion.confidence || 0)),
        reasoning: Array.isArray(suggestion.reasoning) ? suggestion.reasoning : [],
        conflictWarnings: Array.isArray(suggestion.conflictWarnings) ? suggestion.conflictWarnings : []
      }));
    } catch (error) {
      console.error('Failed to parse AI suggestions:', error);
      return [];
    }
  }

  private parseObservations(response: string): AIObservation[] {
    try {
      const parsed = JSON.parse(response);
      return (parsed.observations || []).map((obs: any) => ({
        type: ['insight', 'warning', 'recommendation'].includes(obs.type) ? obs.type : 'insight',
        title: obs.title || 'Untitled Observation',
        description: obs.description || '',
        priority: ['high', 'medium', 'low'].includes(obs.priority) ? obs.priority : 'medium',
        affectedClasses: Array.isArray(obs.affectedClasses) ? obs.affectedClasses : [],
        suggestedActions: Array.isArray(obs.suggestedActions) ? obs.suggestedActions : []
      }));
    } catch (error) {
      console.error('Failed to parse AI observations:', error);
      return this.generateFallbackObservations({} as any, []);
    }
  }

  private generateFallbackObservations(grid: StaffAllocationGrid, staff: Staff[]): AIObservation[] {
    const observations: AIObservation[] = [];
    
    // Basic analysis without AI
    const unassignedCount = grid.classes?.filter(cls => 
      !Object.values(cls.lineAssignments || {}).some(assignment => assignment)
    ).length || 0;

    if (unassignedCount > 0) {
      observations.push({
        type: 'warning',
        title: 'Unassigned Classes',
        description: `${unassignedCount} classes remain unassigned and need teacher allocation.`,
        priority: 'high',
        affectedClasses: [],
        suggestedActions: ['Review teacher availability', 'Consider hiring additional staff']
      });
    }

    return observations;
  }

  private calculateOverallConfidence(suggestions: AIAssignmentSuggestion[]): number {
    if (suggestions.length === 0) return 0;
    const totalConfidence = suggestions.reduce((sum, s) => sum + s.confidence, 0);
    return Math.round(totalConfidence / suggestions.length);
  }

  private getDepartmentDistribution(staff: Staff[]) {
    const distribution: Record<string, number> = {};
    staff.forEach(teacher => {
      const dept = teacher.department || 'Unknown';
      distribution[dept] = (distribution[dept] || 0) + 1;
    });
    return distribution;
  }

  private getFTEDistribution(staff: Staff[]) {
    const distribution = { full: 0, partial: 0 };
    staff.forEach(teacher => {
      if (teacher.fte >= 1.0) {
        distribution.full++;
      } else {
        distribution.partial++;
      }
    });
    return distribution;
  }

  private calculatePreferencesCovered(intentions: TeachingIntention[], grid: StaffAllocationGrid) {
    // Simplified calculation - would need more complex logic in practice
    return Math.round(intentions.length * 0.7); // Placeholder
  }
}

export const openaiService = new OpenAIService();